import SideModal from "components/SideModal";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  addVerdictInitialDay,
  fetchAppealCouncilDecisionTypes,
  fetchAppealDecisionMeasureTypes,
} from "services/prosecutorDecipilineService";
import { Appeal, Complaints, CouncilLitigation } from "types";
import { LoadingIcon } from "utils/constant";
import "../../complaintRequest/styles/AddEvidenceModal.css";
import { useTranslation } from "contexts/i18nContext";

interface DecisionModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  councilLitigationData?: CouncilLitigation;
  appealData?: Appeal;
  complaintData: Complaints;
  onRefresh: any;
}

export default function AddCouncilOfficeVerdictInitialDay({
  isOpen,
  setIsOpen,
  councilLitigationData,
  appealData,
  complaintData,
  onRefresh,
}: DecisionModalProps) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const authData = useAuth();

  const [stampUrl, setStampUrl] = useState<string | null>(null);
  const [stampBlob, setStampBlob] = useState<Blob | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const [formState, setFormState] = useState({
    files: [] as File[],
    given_order: "",
    council_decision_summary: "",
    council_decision_day: "",
    council_decision_type: "",
    discipline_measure_type: "",
    remaining_days_for_appeal: "",
    verdict_given_on_intialday_ins: false,
    verdict_given_on_intialday_accused: false,
  });
  const [decisionTypes, setDecisionTypes] = useState<any>([]);
  // const [decisionMeasureTypes, setDecisionMeasureTypes] = useState<any>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadDecisionTypes = useCallback(async () => {
    try {
      const resData = await fetchAppealCouncilDecisionTypes();
      if (resData) {
        setDecisionTypes(resData);
      }
    } catch (err) {
      showToast(t("failedToLoadDecisionTypes"), "error");
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  const loadDecisionMeasureTypes = useCallback(async () => {
    try {
      const resData = await fetchAppealDecisionMeasureTypes();
      if (resData) {
        // setDecisionMeasureTypes(resData);
      }
    } catch (err) {
      showToast(t("failedToLoadMeasureTypes"), "error");
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    let objectUrl: string | null = null;

    const loadStamp = async () => {
      try {
        // Construct the actual API URL – adjust to your real endpoint
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL_2}organization/service-stamp/read_stamp/?admin_unit_uuid=${user.admin_unit}&service_name=pdms`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
            }, // if needed
          },
        );
        if (!response.ok) throw new Error("Failed to fetch stamp");
        const blob = await response.blob(); // ✅ direct binary, no string corruption
        setStampBlob(blob);
        objectUrl = URL.createObjectURL(blob);
        setStampUrl(objectUrl);
      } catch (error) {
        console.error("Failed to load stamp", error);
      }
    };

    if (isOpen && user) loadStamp();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isOpen, user]);

  useEffect(() => {
    loadDecisionTypes();
    loadDecisionMeasureTypes();
  }, [loadDecisionTypes, loadDecisionMeasureTypes]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        files: [...prev.files, ...Array.from(e.target.files as FileList)],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!councilLitigationData && !appealData) return;

    if (!stampBlob) {
      setValidationError("Stamp is required.");
      setLoading(false);
      return;
    }

    setLoading(true);

    const data = new FormData();
    data.append(
      "initial_day_council_decision_files",
      formState?.files[0] || "",
    );
    data.append("signature1", stampBlob, "stamp.png");

    data.append("council_decision_summary", formState.council_decision_summary);
    data.append("council_decision_day", formState.council_decision_day);
    data.append("council_decision_type", formState.council_decision_type);
    // data.append("discipline_measure_type", formState.discipline_measure_type);
    if (formState.given_order)
      data.append("given_order", formState.given_order || "");
    // if (formState.remaining_days_for_appeal)
    //   data.append(
    //     "remaining_days_for_appeal",
    //     String(formState.remaining_days_for_appeal)
    //   );
    // data.append(
    //   "is_final_verdict_given",
    //   String(councilLitigationData?.is_final_verdict_given)
    // );
    data.append("admin_unit_id", authData?.user?.admin_unit || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append(
      "accused_prosecutor_name",
      complaintData?.accused_prosecutor_name || "",
    );
    data.append(
      "user_id_accused_prosecutor_name",
      complaintData?.user_id_accused_prosecutor_name || "",
    );
    // data.append(
    //   "accused_prosecutor_org_id",
    //   complaintData?.accused_prosecutor_org_id || ""
    // );
    // data.append(
    //   "accused_prosecutor_admin_unit_id",
    //   complaintData?.accused_prosecutor_admin_unit_id || ""
    // );
    data.append(
      "verdict_given_on_intialday_ins",
      String(formState.verdict_given_on_intialday_ins),
    );

    if (councilLitigationData)
      data.append("original_verdict", councilLitigationData?.id || "");
    if (appealData)
      data.append("appeal_request_ins_prosecutor", appealData?.id || "");

    const isFormData = true; // flag indicating multipart submission

    try {
      await addVerdictInitialDay(data, isFormData);

      onRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      setFormState({
        files: [] as File[],
        given_order: "",
        council_decision_summary: "",
        council_decision_day: "",
        council_decision_type: "",
        discipline_measure_type: "",
        remaining_days_for_appeal: "",
        verdict_given_on_intialday_ins: false,
        verdict_given_on_intialday_accused: false,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("addCouncilDecision")}
    >
      <form onSubmit={handleSubmit} className="evidence-form">
        {validationError && (
          <div className="mb-4 text-red-600 font-medium text-sm border border-red-200 bg-red-50 rounded p-2">
            {validationError}
          </div>
        )}
        <section className="evidence-section">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("councilDecisionDate")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type={"date"}
              name="council_decision_day"
              placeholder={t("egJune122014")}
              value={formState.council_decision_day}
              onChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("givenOrder")} ({t("optional")})
            </label>
            <textarea
              rows={2}
              name="given_order"
              value={formState.given_order}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>

          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {t("councilDecisionType")}
            </label>
            <select
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              name="council_decision_type"
              value={formState.council_decision_type}
              onChange={handleChange}
            >
              <option value="">{t("selectDisciplineType")}</option>
              {decisionTypes?.map((list: any) => (
                <option key={list.id} value={list.decision_type}>
                  {list.decision_type}
                </option>
              ))}
            </select>
          </div>

          {/* <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Decision Measure Type
            </label>
            <select
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              name="discipline_measure_type"
              value={formState.discipline_measure_type}
              onChange={handleChange}
            >
              <option value="">Select Discipline Measure</option>
              {decisionMeasureTypes?.map((list: any) => (
                <option key={list.id} value={list.measure}>
                  {list.measure}
                </option>
              ))}
            </select>
          </div> */}

          {/* <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remaining Days For Appeal
              </label>
              <input
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                type="number"
                min={0}
                name="remaining_days_for_appeal"
                value={formState.remaining_days_for_appeal}
                onChange={handleChange}
                placeholder="e.g. 30"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Initial Day Verdict
              </label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    checked={formState.verdict_given_on_intialday_ins}
                    onChange={(e) =>
                      setFormState((p) => ({
                        ...p,
                        verdict_given_on_intialday_ins: e.target.checked,
                      }))
                    }
                  />
                  Institution
                </label>
                <label className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    checked={formState.verdict_given_on_intialday_accused}
                    onChange={(e) =>
                      setFormState((p) => ({
                        ...p,
                        verdict_given_on_intialday_accused: e.target.checked,
                      }))
                    }
                  />
                  Accused
                </label>
              </div>
            </div>
          </div> */}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("councilDecisionSummary")}
            </label>
            <textarea
              required
              rows={3}
              name="council_decision_summary"
              value={formState.council_decision_summary}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>
        </section>

        <section className="evidence-section">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("councilDecisionFile")}
          </label>
          <div className="file-upload-area" onClick={triggerFileInput}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              multiple
              style={{ display: "none" }}
            />
            <p className="upload-text">{t("uploadFiles")}</p>
            {formState.files.length > 0 && (
              <div className="file-list">
                {formState.files.map((file, index) => (
                  <div key={index} className="file-item">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {stampUrl && (
          <div className="my-4 flex items-center justify-center">
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3 shadow-sm">
              <svg
                className="w-6 h-6 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-green-700 font-semibold">
                Stamp fetched successfully
              </span>
            </div>
          </div>
        )}

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}...
            </div>
          ) : (
            t("addDecision")
          )}
        </button>
      </form>
    </SideModal>
  );
}
